'use client';

import { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useRouter } from '@/lib/navigation';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  // Create axios instance with base URL
  const api = axios.create({
    baseURL: `${process.env.NEXT_PUBLIC_SOCKET_URL || 'http://localhost:4000'}/api/auth`,
  });

  // Check if user is logged in on mount
  useEffect(() => {
    const checkLoggedIn = async () => {
      const token = localStorage.getItem('token');

      if (token) {
        try {
          const config = {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };

          const { data } = await api.get('/me', config);
          setUser(data);
          
          // Sync with legacy game logic
          localStorage.setItem('userId', data._id);
          localStorage.setItem('username', data.username);
          if (data.activeTitle) {
            localStorage.setItem('activeTitle', data.activeTitle);
          } else {
            localStorage.removeItem('activeTitle');
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    checkLoggedIn();
  }, []);

  const register = async (userData) => {
    const response = await api.post('/register', userData);
    return response.data;
  };

  const verify = async (verifyData) => {
    const response = await api.post('/verify', verifyData);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      setUser(response.data);
      localStorage.setItem('userId', response.data._id);
      localStorage.setItem('username', response.data.username);
      if (response.data.activeTitle) {
        localStorage.setItem('activeTitle', response.data.activeTitle);
      } else {
        localStorage.removeItem('activeTitle');
      }
    }
    return response.data;
  };

  const login = async (userData) => {
    const response = await api.post('/login', userData);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      setUser(response.data);
      localStorage.setItem('userId', response.data._id);
      localStorage.setItem('username', response.data.username);
      if (response.data.activeTitle) {
        localStorage.setItem('activeTitle', response.data.activeTitle);
      } else {
        localStorage.removeItem('activeTitle');
      }
    }
    return response.data;
  };

  const forgotPassword = async ({ email, locale }) => {
    const response = await api.post('/forgot-password', { email, locale });
    return response.data;
  };

  const verifyResetPin = async ({ email, pin }) => {
    const response = await api.post('/verify-reset-pin', { email, pin });
    return response.data;
  };

  const resetPassword = async ({ resetToken, newPassword }) => {
    const response = await api.post('/reset-password', { resetToken, newPassword });
    return response.data;
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const { data } = await api.get('/me', { headers: { Authorization: `Bearer ${token}` } });
      setUser(data);
    } catch (error) {
      console.error('refreshUser failed:', error);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    // We might want to keep userId/username for guest mode, 
    // or clear them to force new guest session. 
    // For now, let's clear to ensure clean state.
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    setUser(null);
    router.push('/');
  };

  return (
    <AuthContext.Provider value={{ user, setUser, register, verify, login, logout, loading, forgotPassword, verifyResetPin, resetPassword, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
